import axios from 'axios';
import type { AssistantTransport } from '@speles7172/assistant-console';
import type {
  AssistantNote,
  AssistantQuestion,
  BriefingState,
  EntityRef,
} from '@speles7172/assistant-console';
import { extractApiErrorMessage } from '../utils/apiError';
import { apiClient } from './client';

/**
 * The AI assistant's transport (`AssistantTransport`) over `/assistant` —
 * producers and administrators only. A module-level object, so its identity
 * never changes: the console keys its loads on it.
 *
 * Notes, questions and briefings are jobs on the server; every call here
 * answers at once and the console polls the row until it is done.
 */
const BASE = '/assistant';

async function said<T>(work: Promise<{ data: T }>): Promise<T> {
  try {
    return (await work).data;
  } catch (err) {
    throw new Error(extractApiErrorMessage(err, 'The assistant could not be reached. Try again.'));
  }
}

/** A recording goes straight to S3 on a presigned PUT, then the note names its key. */
async function uploadRecording(
  audio: Blob,
): Promise<{ audioKey: string; audioContentType: string }> {
  const contentType = (audio.type || 'audio/webm').split(';')[0] as string;
  const { uploadUrl, s3Key } = await said(
    apiClient.post<{ uploadUrl: string; s3Key: string }>(`${BASE}/notes/audio-upload-url`, {
      contentType,
    }),
  );
  try {
    await axios.put(uploadUrl, audio, { headers: { 'Content-Type': contentType } });
  } catch {
    throw new Error('The recording could not be uploaded. Try again.');
  }
  return { audioKey: s3Key, audioContentType: contentType };
}

export const assistantTransport: AssistantTransport = {
  briefing: ({ language, refresh }) =>
    said(
      apiClient.get<BriefingState>(`${BASE}/briefing`, {
        params: { language, ...(refresh ? { refresh: 'true' } : {}) },
      }),
    ),

  listNotes: ({ entity, limit, before }) =>
    said(
      apiClient.get<AssistantNote[]>(`${BASE}/notes`, {
        params: {
          ...(entity ? { entityType: entity.type, entityId: entity.id } : {}),
          ...(limit ? { limit } : {}),
          ...(before ? { before: before.createdAt, beforeId: before.id } : {}),
        },
      }),
    ),

  async createNote({ text, scope, audio }) {
    const recording = audio ? await uploadRecording(audio) : {};
    return said(apiClient.post<AssistantNote>(`${BASE}/notes`, { text, scope, ...recording }));
  },

  getNote: (id) => said(apiClient.get<AssistantNote>(`${BASE}/notes/${encodeURIComponent(id)}`)),

  updateNote: (id, patch) =>
    said(apiClient.patch<AssistantNote>(`${BASE}/notes/${encodeURIComponent(id)}`, patch)),

  async deleteNote(id) {
    await said(apiClient.delete(`${BASE}/notes/${encodeURIComponent(id)}`));
  },

  searchEntities: (term) =>
    said(apiClient.get<EntityRef[]>(`${BASE}/entities`, { params: { q: term } })),

  ask: (question) => said(apiClient.post<AssistantQuestion>(`${BASE}/questions`, { question })),

  getQuestion: (id) =>
    said(apiClient.get<AssistantQuestion>(`${BASE}/questions/${encodeURIComponent(id)}`)),

  listQuestions: (limit) =>
    said(apiClient.get<AssistantQuestion[]>(`${BASE}/questions`, { params: { limit } })),

  // Only the action's address crosses: the server reads the action itself back
  // from the note, question or briefing it belongs to.
  runAction: ({ source, sourceId, action }) =>
    said(
      apiClient.post<{ message: string; url?: string }>(`${BASE}/actions/run`, {
        source,
        sourceId,
        actionId: action.id,
      }),
    ),

  async dismissAction({ source, sourceId, actionId }) {
    await said(apiClient.post(`${BASE}/actions/dismiss`, { source, sourceId, actionId }));
  },
};
